import { AdminHeader } from "@/components/AdminHeader";
import { useEffect, useState } from "react";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Search } from "lucide-react";

const ENGINES = [
  { key: "google-site-verification", label: "Google Search Console", hint: "Search Console → Settings → Ownership verification → HTML tag" },
  { key: "msvalidate.01", label: "Bing / Microsoft", hint: "Bing Webmaster Tools → Add site → HTML meta tag" },
  { key: "yandex-verification", label: "Yandex", hint: "Yandex Webmaster → Verification → Meta tag" },
  { key: "p:domain_verify", label: "Pinterest", hint: "Pinterest → Settings → Claim website → HTML tag" },
  { key: "baidu-site-verification", label: "Baidu", hint: "Baidu Webmaster Tools → Site verification" },
  { key: "naver-site-verification", label: "Naver", hint: "Naver Search Advisor → Site verification" },
  { key: "facebook-domain-verification", label: "Facebook / Meta", hint: "Meta Business Suite → Brand Safety → Domains" },
];

export default function SeoTab() {
  const [tags, setTags] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/seo-meta").then((r) => setTags(r.data || {})).catch(() => {});
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/seo-meta", { tags });
      toast.success(`Saved — ${data.count} verification tag${data.count === 1 ? "" : "s"} active. Run update.sh on the server to embed them in the live page HTML.`);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="admin-seo-tab" className="max-w-2xl">
      <AdminHeader testid="admin-header-seo" eyebrow="Website" title="SEO & Verification" description="Paste the verification code from each search engine's webmaster tools. The matching meta tag is then served on every page." />
      <div className="bg-white shadow-[0_8px_30px_rgba(0,0,0,0.04)] rounded-2xl p-6 space-y-5">
        {ENGINES.map(({ key, label, hint }) => (
          <div key={key}>
            <Label className="font-ui text-xs text-muted-foreground">{label}</Label>
            <Input
              data-testid={`seo-tag-${key}`}
              value={tags[key] || ""}
              onChange={(e) => setTags({ ...tags, [key]: e.target.value })}
              placeholder={`<meta name="${key}" content="…" />`}
              className="input-dark mt-1.5 font-mono text-xs"
            />
            <p className="mt-1 text-[11px] text-muted-foreground">{hint}</p>
          </div>
        ))}
        <Button data-testid="seo-save-button" onClick={save} disabled={saving} className="w-full rounded-full bg-[#C71E2E] text-white hover:bg-[#A01825] font-ui hover:scale-95 transition-transform">
          <Search size={14} className="mr-2" /> {saving ? "Saving…" : "Save Verification Tags"}
        </Button>
        <p className="text-[11px] text-muted-foreground leading-relaxed">
          Note: the preview site picks tags up instantly. The live site (easydrivecarrental.com) embeds them into the raw page HTML the next time you run update.sh — search engines require the tag in the raw HTML to verify ownership.
        </p>
      </div>
    </div>
  );
}
